import { useCallback, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import AllergyEmptyState from "assets/images/alleries-empty-state.svg";
import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { IntoleranceFormModal } from "features/patients/components/IntoleranceFormModal/IntoleranceFormModal";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useIntoleranceMutations } from "features/patients/hooks/useIntoleranceMutations";
import { useIntolerances } from "features/patients/hooks/useIntolerances";

import s from "./Intolerance.module.css";

const formatLabel = (value) => {
  if (!value) return "-";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const Intolerances = () => {
  const patientId = useCurrentPatientId();

  const [isModalOpen, setIsModalOpen] = useState({ open: false, id: null });
  const [intoleranceToDisable, setIntoleranceToDisable] = useState(null);
  const [filters, setFilters] = useState({});

  const { data = [], isLoading } = useIntolerances(patientId);
  const { disableIntolerance } = useIntoleranceMutations(patientId);

  const filteredData = useMemo(
    () =>
      filters.showDisabled === "true"
        ? data
        : data.filter((item) => !item.disabled),
    [data, filters.showDisabled],
  );

  const filtersConfig = [
    {
      id: "showDisabled",
      label: "",
      type: "single",
      options: [{ value: "true", label: "Show deleted" }],
    },
  ];

  const handleOpenModal = useCallback((id) => {
    setIsModalOpen({ open: true, id: typeof id === "number" ? id : null });
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        width: "minmax(200px, 1fr)",
        render: (item) => item.name || "-",
        getTooltipText: (item) => item.name || "-",
      },
      {
        id: "allergyReaction",
        header: "Symptoms",
        width: "200px",
        render: (item) => {
          if (!item.allergyReaction) return "-";
          const c = PILL_COLORS.reactionUnknown;
          return (
            <Pill color={c.color} bg={c.bg} border={c.border}>
              {formatLabel(item.allergyReaction)}
            </Pill>
          );
        },
      },
    ],
    [],
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Edit",
        icon: Pencil,
        primary: true,
        onClick: (item) => handleOpenModal(item.id),
      },
      {
        label: "Delete",
        icon: Trash2,
        primary: true,
        visibleWhen: (item) => !item.disabled,
        onClick: (item) => setIntoleranceToDisable(item),
      },
    ],
    [handleOpenModal],
  );

  return (
    <>
      <div className={s.topWrapper}>
        <h3 className={s.title}>Intolerance</h3>
        <Button variant="text" onClick={handleOpenModal} endIcon={Plus}>
          Add new
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={filteredData}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyIllustration={AllergyEmptyState}
        emptyTitle="No Known Intolerances"
        emptyText="Once a doctor adds an intolerance, it will appear here."
        rowActions={rowActions}
        getRowClassName={(row) => (row.disabled ? "disabled" : undefined)}
        rowsPerPage={5}
        rowsOptions={[5, 10, 20, 50, 100]}
        filtersConfig={filtersConfig}
        filters={{
          value: filters,
          onChange: (groupId, value) =>
            setFilters((prev) => ({ ...prev, [groupId]: value })),
        }}
      />

      <IntoleranceFormModal
        isOpen={isModalOpen.open}
        editId={isModalOpen.id}
        data={data}
        patientId={patientId}
        onClose={() => setIsModalOpen({ open: false, id: null })}
      />

      <ConfirmDialog
        isOpen={!!intoleranceToDisable}
        onClose={() => setIntoleranceToDisable(null)}
        onConfirm={() => {
          if (intoleranceToDisable) {
            disableIntolerance.mutate(intoleranceToDisable.id, {
              onSuccess: () => setIntoleranceToDisable(null),
            });
          }
        }}
        isLoading={disableIntolerance.isPending}
        title="Delete intolerance"
        message="Are you sure you want to delete this intolerance? This action cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );
};

export { Intolerances };
